import { useEffect, useState } from "react";
import { initializeThemeController } from "../utils/themeController";

// Custom hook to manage theme initialization and state
export const useTheme = () => {
  const [currentTheme, setCurrentTheme] = useState("modern");
  const [currentPrimaryColor, setCurrentPrimaryColor] = useState("#000000");
  const [isThemeSystemLoaded, setIsThemeSystemLoaded] = useState(false);

  useEffect(() => {
    // Initialize theme system
    const initializeThemeSystem = () => {
      try {
        // Initialize theme controller from imported module
        initializeThemeController();

        setIsThemeSystemLoaded(true);

        // Get initial theme
        if (window.JGThemeController) {
          const theme = window.JGThemeController.currentTheme;
          setCurrentTheme(theme);
          const primaryColor =
            window.JGThemeController.getCurrentPrimaryColor();
          setCurrentPrimaryColor(primaryColor);
        }
      } catch (error) {
        console.error("Error initializing theme system:", error);
        // Fallback to basic theme application
        const savedTheme =
          localStorage.getItem("jewellery_guru_theme") || "modern";
        document.documentElement.setAttribute("data-theme", savedTheme);
        setCurrentTheme(savedTheme);
      }
    };

    // Initialize theme system
    initializeThemeSystem();

    // Listen for theme changes
    const handleThemeChange = (e) => {
      setCurrentTheme(e.detail.theme);
      if (window.JGThemeController) {
        setCurrentPrimaryColor(
          window.JGThemeController.getCurrentPrimaryColor(),
        );
      }
    };

    document.addEventListener("themeChanged", handleThemeChange);

    return () => {
      document.removeEventListener("themeChanged", handleThemeChange);
    };
  }, []);

  const changeTheme = (themeName) => {
    if (window.JGThemeController) {
      window.JGThemeController.applyTheme(themeName);
    } else {
      // Fallback if theme controller not loaded yet
      document.documentElement.setAttribute("data-theme", themeName);
      localStorage.setItem("jewellery_guru_theme", themeName);
      setCurrentTheme(themeName);
    }
  };

  const addCustomTheme = (key, name, description, variables, category) => {
    if (window.JGThemeController) {
      window.JGThemeController.addTheme(
        key,
        name,
        description,
        variables,
        category,
      );
    }
  };

  const changePrimaryColor = (primaryColor) => {
    if (window.JGThemeController) {
      window.JGThemeController.applyPrimaryColor(primaryColor);
      setCurrentPrimaryColor(window.JGThemeController.getCurrentPrimaryColor());
    }
  };

  return {
    currentTheme,
    currentPrimaryColor,
    changeTheme,
    changePrimaryColor,
    addCustomTheme,
    isThemeSystemLoaded,
  };
};

// Theme constants for easy access
export const THEMES = {
  MODERN: "modern",
  VINTAGE: "vintage",
  PREMIUM: "premium",
};

export default useTheme;
